import { useAuth } from '@clerk/react';
import {
  useMe,
  useOwnerPortalPreviewSession,
  useTrpcMutation,
  useTeam,
} from '@vrplatform/data';
import { UserIcon } from '@vrplatform/icons';
import { Notification } from '@mantine/core';
import { Global } from '@mantine/emotion';
import { formatOwnerName, formatUserName } from '@vrplatform/ui-common';
import type { ReactNode } from 'react';
import { useOwnerPortalSelectedContact } from '../routes/dashboard/useOwnerPortalSelectedContact';

export const OwnerImpersonationNotification = () => {
  const { actor } = useAuth();
  const previewSession = useOwnerPortalPreviewSession();

  if (previewSession)
    return <OwnerPortalPreviewNotification onExit={previewSession.exit} />;

  if (!actor?.sub) return null;

  return <ClerkImpersonationNotification actorClerkUserId={actor.sub} />;
};

const ClerkImpersonationNotification = ({
  actorClerkUserId,
}: {
  actorClerkUserId: string;
}) => {
  const [{ id: teamId }] = useTeam();
  const { signOut } = useAuth();
  const { firstName, lastName } = useMe();

  const { loading, mutate } = useTrpcMutation('resetImpersonation');

  const resetPreview = async () => {
    const res = await mutate({
      actorClerkUserId,
      tenantId: teamId,
    });

    signOut({
      redirectUrl: res.url,
    });
  };

  return (
    <PreviewNotification
      title="Owner Impersonation"
      loading={loading}
      onClose={resetPreview}
    >
      You are previewing as{' '}
      <b>
        {formatUserName(
          { firstName, lastName },
          { lastNameFirst: false, showEmpty: true }
        ) || '-'}
      </b>
      .
    </PreviewNotification>
  );
};

// A preview token session belongs to the manager, so the owner's name comes
// from the previewed contact rather than `/me`.
const OwnerPortalPreviewNotification = ({ onExit }: { onExit: () => void }) => {
  const { data: contact, isLoading } = useOwnerPortalSelectedContact();

  return (
    <PreviewNotification
      title="Owner Portal Preview"
      loading={isLoading}
      onClose={onExit}
      closeLabel="Exit preview"
    >
      You are previewing the owner portal as{' '}
      <b>{formatOwnerName(contact, { lastNameFirst: false }) || '-'}</b>.
      Changes are disabled.
    </PreviewNotification>
  );
};

const PreviewNotification = ({
  children,
  closeLabel,
  loading,
  onClose,
  title,
}: {
  children: ReactNode;
  closeLabel?: string;
  loading: boolean;
  onClose: () => void;
  title: string;
}) => (
  <>
    <Notification
      radius={10}
      icon={<UserIcon color="white" size={16} />}
      color="dark"
      loading={loading}
      title={title}
      onClose={onClose}
      closeButtonProps={closeLabel ? { 'aria-label': closeLabel } : undefined}
      withBorder
      styles={(theme) => ({
        root: {
          position: 'absolute',
          transition: 'left 0.3s ease-out',
          top: theme.spacing.sm,
          left: '50%',
          transform: 'translateX(-50%)',
        },
      })}
    >
      {children}
    </Notification>
    <Global
      styles={() => ({
        '.intercom-lightweight-app, .cl-impersonationFab': {
          display: 'none',
        },
      })}
    />
  </>
);
